import React, { useCallback, useContext } from 'react';
import SegmentedToggle from 'components/SegmentedToggle';
import { useTranslation } from 'react-i18next';
import ResolvePagination from 'components/ResolvePagination';
import ReportLog from './components/ReportLog';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import ReportsEmptyState from './components/ReportsEmptyState';
import { SIZE } from 'models/Size';
import ReportsFilters from './components/ReportsFilters';
import { getIsArrayEmpty } from 'utils';
import { Loader } from 'ncoded-component-library';
import NoResultBanner from 'components/NoResultBanner';
import { NAVIGATION_VARIANTS } from 'models/Navigation';
import useReportsQuery from './hooks/useReportsQuery';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';

import './Reports.styles.scss';

const Reports: React.FC = () => {
  const {
    dataRoom: { _id: dataRoomId },
    isPrefferedSummarisedView,
    setDataRoom,
  } = useContext(DataRoomContext);

  const { hasActiveFilter, reports, totalPages, isLoading } = useReportsQuery({
    dataRoomId,
    isSummarised: isPrefferedSummarisedView,
  });

  const noReports = getIsArrayEmpty(reports);

  const shouldRenderEmptyState = !isLoading && !hasActiveFilter && noReports;

  const { t } = useTranslation();

  const updateActivityLogPreview = useCallback(
    (isPrefferedSummarisedView: boolean) => {
      setDataRoom((old) => ({
        ...old,
        currentMember: { ...old.currentMember, isPrefferedSummarisedView },
      }));

      dataRoomApi.room.updatePreferredValues(dataRoomId, {
        isPrefferedSummarisedView,
      });
    },
    [dataRoomId, setDataRoom],
  );

  return (
    <div className="reports-page__container white-panel">
      {shouldRenderEmptyState ? (
        <ReportsEmptyState size={SIZE.BIG} />
      ) : (
        <>
          <div className="reports-page__container__filters">
            <ReportsFilters variant={NAVIGATION_VARIANTS.TERTIARY} />

            <SegmentedToggle
              options={[
                { label: t('detailed'), value: false },
                { label: t('summarised'), value: true },
              ]}
              selectedValue={isPrefferedSummarisedView}
              onChange={(value) => updateActivityLogPreview(!!value)}
            />
          </div>
          {isLoading ? (
            <Loader />
          ) : noReports ? (
            <NoResultBanner title={t('noResultsWithReportFilters')} />
          ) : (
            <>
              <div className="reports-page__container__logs">
                {reports.map((report) => (
                  <ReportLog
                    key={report._id}
                    report={report}
                    isSummarised={isPrefferedSummarisedView}
                  />
                ))}
              </div>
              <ResolvePagination totalPages={totalPages} />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Reports;
